import React, { useState, useEffect, useRef } from "react";
import { Redirect, Route, Router, Switch } from "react-router-dom";
import "./App.scss";
import "../styles/sharedStyles.scss";
import Nsc from "./pure/Nsc";
import GlobalNavBar from "../shared/globalNavBar/GlobalNavBar";
import Footer from "../shared/Footer/Footer";
import QuickStart from "../components/quickStart/QuickStart";
import ProgressBarTabs from "../components/progressBar/ProgressBarTabs";
import InvalidRoute from "../components/accessDenied/InvalidRoute";
import Reporting from "../components/reporting/Reporting";
import BusinessSettings from "../components/businessSetting/BusinessSettings";
import { logMsg } from "../common/Logger";
import withIdleTimer from "../common/WithIdleTimer";
import AccessDeniedNotOnboardedNsc from "../components/accessDenied/AccessDeniedNotOnboardedNsc";
import HasPermissionTo from "../common/HasPermissionTo";
import NscDashboard from "../components/dashboard/nscDashBoard/NscDashboard";
import NscOnboardingClient from "../shared/clients/NscOnboardingClient";
import LocalizedInfoClient from "../shared/clients/LocalizedInfoClient";
import ErrorScreen from "../errors/ErrorScreen";
import { IS_SA_MARKET } from "../utils/EmpUtil";

const NSCApp = (props) => {
  const [renderApp, setRenderApp] = useState(false);
  const [shouldNavigationBeRestricted, setShouldNavigationBeRestricted] =
    useState(false);
  const [padding, setPaddingState] = useState("70px");

  const userRef = useRef(new Nsc(props.userDetails, props.history));
  const localizedInfoRef = useRef({});
  const onBoardingStatusRef = useRef(null);

  useEffect(() => {
    getLocalizedInfo();

    const unlisten = props.history.listen(() => setNavigationRestriction());

    return () => {
      unlisten();
    };
  }, []);

  const onReservationSave = () => {
    onBoardingStatusRef.current = "ACCEPTED";
    navigateToRoute("dashboard");
  };

  const navigateToRoute = (link) => {
    props.history.push(`/${link}`);
  };

  const setNavigationRestriction = () => {
    if (onBoardingStatusRef.current !== "ACCEPTED") {
      setShouldNavigationBeRestricted(true);
    } else {
      const isAllowedPath = isCurrentPathAllowedToShowHeaderAndFooter(
        props.history.location.pathname
      );
      setShouldNavigationBeRestricted(!isAllowedPath);
    }
  };

  const setPadding = (paddingValue) => {
    if (paddingValue > 70) {
      setPaddingState(paddingValue + "px");
    }
  };

  const isCurrentPathAllowedToShowHeaderAndFooter = (currentPathName) => {
    const staticAllowedPaths = [
      "/dashboard",
      "/business-settings",
      "/reporting",
      "/",
    ];

    return staticAllowedPaths.includes(currentPathName);
  };

  const checkIsUserOnboarded = () => {
    isOnboarded()
      .then((val) => {
        onBoardingStatusRef.current = val.onboarded ? "ACCEPTED" : "PENDING";
        setNavigationRestriction();
      })
      .finally(() => {
        userRef.current.hasNscCompletedBasicPAs = true;
        setRenderApp(true);
      });
  };

  const isOnboarded = async () => {
    return await NscOnboardingClient.isOnboarded(userRef.current);
  };

  const getLocalizedInfo = () => {
    props.hideOrShow(true);
    LocalizedInfoClient.getLocalizedInfo(userRef.current)
      .then((response) => {
        localizedInfoRef.current = Object.assign(
          {},
          { ...localizedInfoRef.current },
          { ...response }
        );
      })
      .catch((error) => logMsg("the error is: ", error))
      .finally(() => {
        props.i18n.changeLanguage("en" + "_" + "AUS");
        checkIsUserOnboarded();
        props.hideOrShow(false);
      });
  };

  const getProgressBarTabs = () => {
    return IS_SA_MARKET(userRef.current.market)
      ? ProgressBarTabs().onboardingSANsc
      : ProgressBarTabs().onboardingNsc;
  };

  const ProtectedRoute = ({
    component: Component,
    perform: perform,
    ...rest
  }) => (
    <HasPermissionTo
      perform={perform}
      permissions={userRef.current.permissions.rolePermissions}
      render={() => (
        <Route
          {...rest}
          render={() =>
            onBoardingStatusRef.current === "ACCEPTED" ? (
              <Component {...rest.customprops} {...props} />
            ) : (
              <Redirect to="/" />
            )
          }
        />
      )}
    />
  );

  return (
    renderApp && (
      <Router history={props.history}>
        <div className="appWrapper" id="appWrapper">
          {!shouldNavigationBeRestricted && (
            <div style={{ paddingTop: padding }}>
              <GlobalNavBar
                {...props}
                user={userRef.current}
                urlPath={window.location.pathname}
                navigateToRoute={navigateToRoute}
                setPadding={setPadding}
              />
            </div>
          )}

          <Switch>
            <ProtectedRoute
              exact
              path="/dashboard"
              component={withIdleTimer(NscDashboard)}
              perform={["dashBoardPage"]}
              customprops={{
                user: userRef.current,
              }}
            />

            <ProtectedRoute
              exact
              path="/reporting"
              component={withIdleTimer(Reporting)}
              perform={["reportingPage"]}
              customprops={{
                user: userRef.current,
              }}
            />
            <ProtectedRoute
              exact
              path="/business-settings"
              component={withIdleTimer(BusinessSettings)}
              perform={["businessSettingsPage"]}
              customprops={{
                user: userRef.current,
                localizedInfo: localizedInfoRef.current,
              }}
            />

            <Route
              exact
              path="/denied-nsc"
              component={AccessDeniedNotOnboardedNsc}
            />
            <Route
              exact
              path="/"
              render={() =>
                onBoardingStatusRef.current === "ACCEPTED" ? (
                  <Redirect
                    to={{
                      pathname: "/dashboard",
                    }}
                  />
                ) : (
                  <HasPermissionTo
                    perform={["quickStartPage"]}
                    permissions={userRef.current.permissions.rolePermissions}
                    render={() => (
                      <QuickStart
                        user={userRef.current}
                        {...props}
                        onboardingClient={NscOnboardingClient}
                        localizedInfo={localizedInfoRef.current}
                        onSave={onReservationSave}
                        progressBarTabs={getProgressBarTabs()}
                        fromQuickstart={true}
                      />
                    )}
                    noRender={() => (
                      <Redirect
                        to={{
                          pathname: "/denied-nsc",
                        }}
                      />
                    )}
                  />
                )
              }
            />
            <Route
              exact
              path="/error"
              render={(routeProps) => <ErrorScreen {...routeProps} />}
            />
            <Route component={withIdleTimer(InvalidRoute)} />
          </Switch>
          {!shouldNavigationBeRestricted && (
            <Footer permissions={props.userDetails.permissions} />
          )}
        </div>
      </Router>
    )
  );
};

export default NSCApp;
